import {loadOsmGraph, routeThrough} from '@/features/navigation/osmGraph'
import {loadNavGrid, polylineLengthMeters, routeOnFloor} from '@/features/navigation/navGrid'
import {distanceMeters} from '@/maps/mapboxDrivingRoute'
import osmGraphData from '@/features/navigation/data/yyzTerminal1OsmGraph.json'
import groundGridData from '@/features/navigation/data/yyzTerminal1GroundLevelGrid.json'

/**
 * THE WALK LINE — three sources, tried in order, best first.
 *
 * ── ⚠️ DECODED ONCE AT MODULE LOAD, NOT PER MOUNT ─────────────────────────────────
 * The baggage-claim grid is 806 x 686 and takes about 11ms to decode. That is invisible
 * once and a visible freeze every time a screen mounts, which is exactly what it did on
 * mobile before this was hoisted. Module scope runs once for the life of the tab.
 */
const graph = loadOsmGraph(osmGraphData)
const groundGrid = loadNavGrid(groundGridData)

/**
 * How far from the walkway network a point may sit and still be snapped onto it.
 *
 * ⚠️ GENEROUS ON PURPOSE AT THE DESTINATION END. A kerbside pickup is not on a walkway
 * centreline and never will be — it is out in the road by design — so a tight radius finds
 * nothing and the whole route falls through to a worse source.
 */
const SNAP_METERS = 120

/** Close enough that appending the real pickup would only add a stub. */
const ENDPOINT_TOLERANCE_METERS = 3

/**
 * The longest separation this will still ask Mapbox to route on foot. See the guard in
 * `fetchWalkingRoute`; it is four times the longest walk Pearson itself can produce.
 */
const MAX_WALKABLE_METERS = 5000

/**
 * @param {[number, number]} start   `[lng, lat]`
 * @param {[number, number]} pickup  `[lng, lat]`
 * @returns {{points: Array<[number, number]>, source: string}|null}
 */
export function buildIndoorRoute(start, pickup) {
    if (!start || !pickup) return null

    /* ── FIRST: THE REAL WALKWAY NETWORK ──────────────────────────────────────────
       Corridor centrelines, so a route physically CANNOT pass through a wall. Everything
       below this is an approximation of what this knows outright. */
    const walked = routeThrough(graph, [start, pickup], SNAP_METERS)
    if (walked?.length) {
        /* ⚠️ THE LINE HAS TO END WHERE THE MARKER IS. Snapping put the last point on the
           nearest walkway node, which at a kerbside pickup can be metres away in the road;
           a passenger following a line that stops short of the pin will stand where the
           line ended and wait. */
        const end = walked[walked.length - 1]
        if (distanceMeters(end, pickup) > ENDPOINT_TOLERANCE_METERS) walked.push(pickup)
        return {points: walked, source: 'osm'}
    }

    /* ── THEN: THE OCCUPANCY GRID ─────────────────────────────────────────────────
       Clearance-weighted A* over one-metre cells, string-pulled into straight runs. It
       routes AROUND furniture the way a person does, but it only knows the floor it was
       generated for. */
    const gridded = routeOnFloor(groundGrid, start, pickup)
    if (gridded?.length) return {points: gridded, source: 'grid'}

    return null
}

/**
 * THE OUTDOOR LEG — Mapbox's own pedestrian routing, for the stretch the building's
 * geometry does not cover.
 *
 * ⚠️ IT IS ALSO THE WHOLE ROUTE AWAY FROM PEARSON. The graph and the grid exist for one
 * terminal; everywhere else this is the only source there is. That is worth knowing when
 * the walk looks oddly straight at another airport — it is not indoor routing, it is a
 * pavement route through a building Mapbox has no interior for.
 *
 * @param {[number, number]} from
 * @param {[number, number]} to
 * @param {string} token  the Mapbox token, from `/config/app`
 */
export async function fetchWalkingRoute(from, to, token) {
    if (!from || !to || !token) return null
    /**
     * ⚠️ NOT EVERY PAIR OF POINTS IS A WALK, AND ASKING ANYWAY COSTS A 422 EACH TIME.
     *
     * Mapbox's walking profile refuses a request it cannot sensibly route — a
     * `422 Unprocessable Content` — and the console fills with failures that read as a
     * broken integration rather than as a question that should never have been asked.
     *
     * It is reachable outside development: `hasArrivalGuide` no longer requires the pickup
     * to BE Pearson (see `arrivalEligibility`), so a from-airport trip anywhere in the world
     * opens the walk while the booth it walks to stays in Toronto.
     *
     * ⚠️ THE GUARD IS HERE AND NOT AT THE CALL SITE, WHICH IS THE WHOLE POINT. Above it sit
     * the two sources that matter at Pearson — the walkway graph and the occupancy grid —
     * and they are local, synchronous and free. A distance check in `WalkToPickup` would
     * have skipped those as well, so a real passenger whose first fix was poor enough to
     * look far from the terminal would lose the INDOOR route rather than a doomed request.
     * This only ever declines to ask the network.
     *
     * The longest walk Pearson can produce is about 1.1km — the international arrivals door
     * to the Terminal 3 pre-arranged booth — so the limit sits well above anything real.
     */
    if (distanceMeters(from, to) > MAX_WALKABLE_METERS) return null
    const coords = `${from[0]},${from[1]};${to[0]},${to[1]}`
    const url = `https://api.mapbox.com/directions/v5/mapbox/walking/${coords}`
        + `?geometries=geojson&overview=full&steps=true&access_token=${token}`

    try {
        const response = await fetch(url)
        if (!response.ok) return null
        const data = await response.json()
        const points = data?.routes?.[0]?.geometry?.coordinates
        return points?.length ? {points, source: 'mapbox'} : null
    } catch {
        /* A failed route is not an error to show. The written directions are a full
           alternative and the photographs do not need a line at all. */
        return null
    }
}

/**
 * The best line available between two points, indoor first.
 *
 * ⚠️ THE INDOOR SOURCES ARE SYNCHRONOUS AND MAPBOX IS NOT, which is why this is async even
 * though the good answer usually arrives without awaiting anything.
 */
export async function buildWalkRoute(start, pickup, token) {
    return buildIndoorRoute(start, pickup) || await fetchWalkingRoute(start, pickup, token)
}

export {polylineLengthMeters}
export default buildWalkRoute
